import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import { IoIosAttach } from "react-icons/io";
import { HiOutlineTrash } from "react-icons/hi";

function formatBytes(a: any, b = 2) {
  if (!+a) return "0 Bytes";
  const c = 0 > b ? 0 : b,
    d = Math.floor(Math.log(a) / Math.log(1024));
  return `${parseFloat((a / Math.pow(1024, d)).toFixed(c))} ${
    ["Bytes", "KB", "MB", "GiB", "TB", "PB", "EB", "ZB", "YB"][d]
  }`;
}

interface UploadProps {
  setFiles: (files: File[]) => void;
}

export function FileUpload({ setFiles }: UploadProps) {
  const [previews, setPreviews] = useState<string[]>([]);
  const [files, setFilesState] = useState<File[]>([]);
  const maxFiles = 6;
  const maxFileSize = 8 * 1024 * 1024;

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    accept: { "image/*": [], "application/pdf": [] },
    onDrop: (newFiles) => {
      if (newFiles.length === 0) {
        toast.error("حداکثر تعداد تصویر مجاز برای یک آزمایش 6 مورد است.");
        return;
      }

      const isPdf =
        newFiles.length === 1 && newFiles[0].type === "application/pdf";
      const isImages = newFiles.every((file) => file.type.startsWith("image/"));

      if (!isPdf && !isImages) {
        toast.error("فقط یک فایل PDF یا حداکثر 6 تصویر قابل آپلود است.");
        return;
      }

      if (isPdf && files.length > 0) {
        toast.error("نمی‌توانید فایل PDF را همراه با تصاویر آپلود کنید.");
        return;
      }

      if (isImages && files.some((file) => file.type === "application/pdf")) {
        toast.error("نمی‌توانید تصویر را همراه با فایل PDF آپلود کنید.");
        return;
      }

      if (isImages && newFiles.length + files.length > maxFiles) {
        toast.error("حداکثر تعداد تصویر مجاز برای یک آزمایش 6 مورد است.");
        return;
      }

      const validFiles = newFiles.filter((file) => {
        if (file.size > maxFileSize) {
          toast.error("حداکثر سایز مجاز برای تصویر 8 مگابایت است.");
          return false;
        }
        return true;
      });

      const updatedFiles = [...files, ...validFiles];
      setFilesState(updatedFiles);
      setFiles(updatedFiles);

      setPreviews(updatedFiles.map((file) => URL.createObjectURL(file)));
    },
  });

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFilesState(updatedFiles);
    setFiles(updatedFiles);
    setPreviews(updatedFiles.map((file) => URL.createObjectURL(file)));
  };

  useEffect(() => {
    if (files.length === 0) {
      setFiles([]);
      setPreviews([]);
    }
  }, [files, setFiles]);

  const previewsList = previews.map((preview, index) => (
    <div
      key={index}
      className="relative w-full h-[160px] flex flex-col items-center rounded-lg border-[1px] border-gray-500 overflow-hidden"
    >
      {files[0].type === "application/pdf" ? (
        <embed
          src={preview}
          type="application/pdf"
          className="w-full h-64 border border-gray-500 rounded-lg"
        />
      ) : (
        <img
          src={preview}
          alt="Preview"
          className="absolute left-0 top-0 rounded-md h-auto w-full z-[1]"
        />
      )}

      <div
        className="absolute left-0 top-0 w-full h-full z-[2]"
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.8) 100%)`,
        }}
      ></div>

      <div className="w-full rounded-md overflow-hidden absolute right-1 top-1 z-[2]">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveFile(index);
          }}
          className="bg-red-500 text-white rounded-full p-1"
        >
          <HiOutlineTrash size={20} />
        </button>
      </div>

      <div className="mt-2 w-[90%] absolute left-[5%] bottom-0 z-[3] p-1 text-white overflow-hidden">
        <p
          className="text-xs text-left whitespace-nowrap"
          style={{ direction: "ltr" }}
        >
          {files[index]?.name}
        </p>
        <p className="text-xs text-left font-bold" style={{ direction: "ltr" }}>
          {formatBytes(files[index]?.size)}
        </p>
      </div>
    </div>
  ));

  return (
    <div className="flex-1 h-full">
      <div className="label">
        <span className="label-text">
          * بارگذاری فایل آزمایش{" "}
          <a
            className="text-pr_primary"
            href="https://bodybon.info/bodybon-medical-test-upload-guideline/"
          >
            (راهنمای بارگذاری عکس آزمایش)
          </a>
          :
        </span>
      </div>
      <div
        {...getRootProps({
          className:
            "dropzone bg-gray-200 border-2 border-gray-300 rounded-lg cursor-pointer",
        })}
      >
        <div
          className={`min-h-[260px] lg:min-h-[436px] px-3 py-8 flex flex-wrap ${
            previewsList.length > 0
              ? "items-start lg:pb-8 pb-20"
              : "items-center pb-8"
          } justify-center relative gap-2 overflow-hidden`}
        >
          {previewsList.length > 0 && (
            <p className="absolute bottom-0 left-0 w-full h-12 rounded-b-lg font-[900] font-rokh bg-white flex items-center justify-center pt-1 z-[10]">
              افزودن تصویر جدید
            </p>
          )}
          <input {...getInputProps()} />
          <>
            <div
              className={`flex flex-wrap gap-2 items-center justify-center rounded-md overflow-hidden w-full`}
            >
              {previews.length > 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 w-full h-full">
                  {previewsList}
                </div>
              ) : (
                <div className="w-full">
                  <div
                    className={`border-[3px] rounded-md w-fit mx-auto border-dashed border-gray-500 p-5`}
                  >
                    <IoIosAttach size={55} className="mx-auto text-gray-500" />
                  </div>
                  <p className="text-center mt-5 text-gray-500 mx-auto font-rokh font-[700]">
                    فایل آزمایش را بارگذاری کنید.
                  </p>
                  <p className="text-center text-gray-500 max-w-xs text-sm mx-auto font-rokh font-[500]">
                    انتخاب یک فایل pdf یا حداکثر ۶ تصویر با فرمت png و jpg قابل
                    قبوله.
                  </p>
                </div>
              )}
            </div>
          </>
        </div>
      </div>
    </div>
  );
}
